import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { defer, forkJoin, Subject, Subscription } from 'rxjs';
import { debounceTime, finalize } from 'rxjs/operators';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { SecurityRoleService } from '@app/modules/security-roles/services/security-role.service';
import { CustomReportsService } from '@app/modules/custom-reports/services/reports.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';

interface RoleOption {
  id: string;
  name: string;
}

// Inline version of AssignReportSecurityDialogComponent for the report designer page - the
// assigned roles are shown up front as chips, with a searchable, paged role list to add more.
@Component({
  selector: 'app-report-security-panel',
  templateUrl: './report-security-panel.component.html',
  styleUrls: ['./report-security-panel.component.scss']
})
export class ReportSecurityPanelComponent implements OnChanges, OnDestroy {
  @Input() reportId: string;

  @Output() dirtyChange = new EventEmitter<boolean>();
  @Output() assignedCountChange = new EventEmitter<number>();

  public roles: RoleOption[] = [];
  public totalRoles = 0;
  public pageSize = 8;
  public skip = 0;
  public searchValue = '';

  public loadingAssigned = false;
  public loadingRoles = false;
  public saving = false;

  // Insertion-ordered so chips appear in the order roles were assigned/selected.
  public selectedRoles = new Map<string, string>();
  private initialRoleIds = new Set<string>();

  private search$ = new Subject<void>();
  private searchSubscription: Subscription;
  private rolesRequest: Subscription | null = null;
  private assignedRequest: Subscription | null = null;

  constructor(
    private snackbarService: SnackbarService,
    private securityRoleService: SecurityRoleService,
    private customReportsService: CustomReportsService,
    private overlayService: OverlayService
  ) {
    this.searchSubscription = this.search$.pipe(debounceTime(250)).subscribe(() => {
      this.skip = 0;
      this.getRoles();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.reportId && this.reportId) {
      this.skip = 0;
      this.searchValue = '';
      this.loadAssignedRoles();
      this.getRoles();
    }
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
    this.rolesRequest?.unsubscribe();
    this.assignedRequest?.unsubscribe();
  }

  get selectedRoleList(): RoleOption[] {
    return Array.from(this.selectedRoles, ([id, name]) => ({ id, name }));
  }

  // selectedRoleList builds new objects on every change detection pass - without this, ngFor
  // re-creates every chip each pass, swapping out the remove button between mousedown and
  // mouseup so its click never fires.
  trackByRoleId(_index: number, role: RoleOption): string {
    return role.id;
  }

  get isDirty(): boolean {
    if (this.selectedRoles.size !== this.initialRoleIds.size) {
      return true;
    }
    return Array.from(this.selectedRoles.keys()).some(id => !this.initialRoleIds.has(id));
  }

  get rangeEnd(): number {
    return Math.min(this.skip + this.pageSize, this.totalRoles);
  }

  private loadAssignedRoles(): void {
    this.assignedRequest?.unsubscribe();
    this.loadingAssigned = true;

    this.assignedRequest = this.customReportsService.getReportsSecurity(100, 0, null, `report.Id = "${this.reportId}"`)
      .pipe(finalize(() => this.loadingAssigned = false))
      .subscribe({
        next: (pagedSecurity) => {
          const assigned = (pagedSecurity?.data || []).map(security => security.role);
          this.selectedRoles = new Map(assigned.map(role => [role.id, role.name]));
          this.initialRoleIds = new Set(assigned.map(role => role.id));
          this.emitState();
        },
        error: () => {
          this.snackbarService.openSnackBar('Unable to load report security', 'clear', 'warn');
        }
      });
  }

  getRoles(): void {
    this.rolesRequest?.unsubscribe();
    this.loadingRoles = true;

    const filter = this.searchValue ? `(Name like "${this.searchValue}")` : null;

    this.rolesRequest = this.securityRoleService.getSecurityRoles(this.pageSize, this.skip, filter, 'name-asc')
      .pipe(finalize(() => this.loadingRoles = false))
      .subscribe(pagedRoles => {
        this.roles = pagedRoles.data.map(role => ({ id: role.id, name: role.name }));
        this.totalRoles = pagedRoles.total;
      });
  }

  onSearch(): void {
    this.search$.next();
  }

  clearSearch(): void {
    this.searchValue = '';
    this.skip = 0;
    this.getRoles();
  }

  changePage(direction: 1 | -1): void {
    this.skip = Math.max(0, this.skip + direction * this.pageSize);
    this.getRoles();
  }

  isSelected(role: RoleOption): boolean {
    return this.selectedRoles.has(role.id);
  }

  toggleRole(role: RoleOption): void {
    if (this.selectedRoles.has(role.id)) {
      this.selectedRoles.delete(role.id);
    } else {
      this.selectedRoles.set(role.id, role.name);
    }
    this.emitState();
  }

  removeRole(roleId: string): void {
    this.selectedRoles.delete(roleId);
    this.emitState();
  }

  save(): void {
    this.overlayService.show();
    const roleIdsToAdd = Array.from(this.selectedRoles.keys()).filter(id => !this.initialRoleIds.has(id));
    const roleIdsToRemove = Array.from(this.initialRoleIds).filter(id => !this.selectedRoles.has(id));

    if (roleIdsToAdd.length === 0 && roleIdsToRemove.length === 0) {
      this.overlayService.hide();
      return;
    }

    const observables = [
      ...roleIdsToAdd.map(roleId => defer(() => this.customReportsService.addReportSecurity(this.reportId, roleId))),
      ...roleIdsToRemove.map(roleId => defer(() => this.customReportsService.removeReportSecurity(this.reportId, roleId))),
    ];

    this.saving = true;

    forkJoin(observables)
      .pipe(finalize(() => {
        this.saving = false;
        this.overlayService.hide();
      }))
      .subscribe({
        next: () => {
          this.snackbarService.openSnackBar('Report security updated successfully', 'clear', 'success');
          this.initialRoleIds = new Set(this.selectedRoles.keys());
          this.emitState();
        },
        error: () => {
          this.snackbarService.openSnackBar('Unable to update report security', 'clear', 'warn');
          // Some calls may have succeeded - re-sync with what the server actually has.
          this.loadAssignedRoles();
        }
      });
  }

  private emitState(): void {
    this.dirtyChange.emit(this.isDirty);
    this.assignedCountChange.emit(this.selectedRoles.size);
  }
}
